<template>
  <div class="ifd-backdrop" @mousedown.self="$emit('close')">
    <div class="ifd" role="dialog" aria-modal="true" aria-label="Find an Inventory page">
      <label class="ifd-input">
        <InvIcon name="search" />
        <input
          ref="q"
          v-model="q"
          type="text"
          placeholder="Find an Inventory page…"
          autocomplete="off"
          spellcheck="false"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="go(active)"
          @keydown.esc.prevent="$emit('close')"
        />
        <kbd>Esc</kbd>
      </label>
      <div class="ifd-list" ref="list">
        <template v-for="g in groups" :key="g.title">
          <p class="ifd-group"><InvIcon :name="g.icon" />{{ g.title }}</p>
          <button
            v-for="it in g.items"
            :key="it.key"
            type="button"
            class="ifd-item"
            :class="{ 'is-on': it.index === active }"
            :data-i="it.index"
            @mouseenter="active = it.index"
            @click="go(it.index)"
          >
            <span class="ifd-icon"><InvIcon :name="it.icon" /></span>
            <span class="ifd-title">{{ it.title }}</span>
            <span v-if="it.parent" class="ifd-parent">{{ it.parent }}</span>
            <InvIcon name="arrow-right" class="ifd-go" />
          </button>
        </template>
        <div v-if="!flat.length" class="ifd-empty">
          <InvArt name="search" />
          <p>No page called “{{ q }}”. Try another word.</p>
        </div>
      </div>
      <div class="ifd-foot">
        <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
        <span><kbd>Enter</kbd> open</span>
        <span class="ifd-count">{{ flat.length }} page{{ flat.length === 1 ? "" : "s" }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import InvIcon from "./InvIcon.vue";
import InvArt from "./InvArt.vue";
import { pageTarget } from "./invNav.js";
import { tr } from "@/i18n";

/** Every Inventory page by name (Ctrl/⌘ K in the workspace), grouped by Kubi's sections. */
export default {
  name: "InvFinder",
  components: { InvIcon, InvArt },
  props: { areas: { type: Array, required: true } },
  emits: ["close"],
  data() {
    return { q: "", active: 0 };
  },
  computed: {
    // The menu's titles in the panel's language: shown, and searched, as the person reads them.
    all() {
      const out = [];
      for (const a of this.areas) {
        for (const p of a.pages) {
          out.push({ key: p.name, title: tr(p.title), area: tr(a.title), areaIcon: a.icon, icon: p.icon, to: pageTarget(p) });
          for (const c of p.children || []) {
            out.push({ key: p.name + ":" + c.title, title: tr(c.title), parent: tr(p.title), area: tr(a.title), areaIcon: a.icon, icon: p.icon, to: pageTarget(c) });
          }
        }
      }
      return out;
    },
    flat() {
      const words = this.q.toLowerCase().split(/\s+/).filter(Boolean);
      const list = words.length
        ? this.all.filter((it) => {
            const hay = (it.title + " " + (it.parent || "") + " " + it.area).toLowerCase();
            return words.every((w) => hay.includes(w));
          })
        : this.all;
      return list.map((it, index) => ({ ...it, index }));
    },
    groups() {
      const out = [];
      for (const it of this.flat) {
        let g = out[out.length - 1];
        if (!g || g.title !== it.area) {
          g = { title: it.area, icon: it.areaIcon, items: [] };
          out.push(g);
        }
        g.items.push(it);
      }
      return out;
    },
  },
  watch: {
    q() {
      this.active = 0;
    },
  },
  mounted() {
    this.$nextTick(() => this.$refs.q && this.$refs.q.focus());
  },
  methods: {
    move(step) {
      const n = this.flat.length;
      if (!n) return;
      this.active = (this.active + step + n) % n;
      this.$nextTick(() => {
        const el = this.$refs.list && this.$refs.list.querySelector('[data-i="' + this.active + '"]');
        if (el) el.scrollIntoView({ block: "nearest" });
      });
    },
    go(i) {
      const it = this.flat[i];
      if (!it) return;
      this.$router.push(it.to);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.ifd-backdrop {
  box-sizing: border-box; position: fixed; inset: 0; z-index: 1300; display: flex; justify-content: center; align-items: flex-start;
  padding: 12vh var(--s-4) var(--s-4); background: var(--bg-overlay, rgba(0, 0, 0, 0.55));
  animation: iw-fade var(--iw-base) linear both;
}
.ifd {
  box-sizing: border-box; width: min(620px, 100%); max-height: 70vh; display: flex; flex-direction: column; overflow: hidden;
  background: var(--bg-modal, var(--bg-primary)); border: 1px solid var(--border-primary); border-radius: var(--r-lg);
  box-shadow: var(--shadow-overlay); animation: iw-pop var(--iw-base) var(--iw-spring) both;
}
.ifd-input { display: flex; align-items: center; gap: var(--s-3); padding: 0 var(--s-4); height: 56px; border-bottom: 1px solid var(--border-primary); color: var(--text-muted); }
.ifd-input .inv-icon { width: 18px; height: 18px; flex: 0 0 auto; }
.ifd-input input {
  flex: 1; height: 100%; border: 0; outline: none; background: transparent; padding: 0;
  font-family: inherit; font-size: var(--t-heading-sm); color: var(--text-primary);
}
.ifd-input input::placeholder { color: var(--text-muted); }
kbd {
  font-family: inherit; font-size: 11px; line-height: 1; font-weight: var(--w-semibold); padding: 3px 6px; margin-right: 2px;
  border-radius: var(--r-sm); border: 1px solid var(--border-primary); background: var(--bg-tertiary); color: var(--text-muted);
}
.ifd-list { overflow-y: auto; padding: var(--s-2); }
.ifd-group {
  display: flex; align-items: center; gap: var(--s-2); margin: var(--s-2) var(--s-2) var(--s-1);
  font-size: var(--t-caption); font-weight: var(--w-semibold); letter-spacing: var(--track-caps); text-transform: uppercase; color: var(--text-muted);
}
.ifd-group .inv-icon { width: 14px; height: 14px; }
.ifd-item {
  display: flex; align-items: center; gap: var(--s-3); width: 100%; height: 44px; padding: 0 var(--s-3);
  border: 0; border-radius: var(--r-md); background: transparent; color: var(--text-primary); cursor: pointer; text-align: left; font-family: inherit;
}
.ifd-item.is-on { background: var(--accent-primary-bg); }
.ifd-icon {
  width: 28px; height: 28px; border-radius: var(--r-md); display: inline-flex; align-items: center; justify-content: center;
  background: var(--bg-tertiary); color: var(--text-secondary); flex: 0 0 auto;
  transition: background var(--iw-fast) var(--iw-ease), color var(--iw-fast) var(--iw-ease);
}
.ifd-item.is-on .ifd-icon { background: var(--gradient-accent); color: #fff; }
.ifd-icon .inv-icon { width: 16px; height: 16px; }
.ifd-title { font-size: var(--t-body); font-weight: var(--w-medium); }
.ifd-parent { font-size: var(--t-caption); color: var(--text-muted); }
.ifd-go { width: 16px; height: 16px; margin-left: auto; color: var(--accent-primary); opacity: 0; transform: translateX(-4px); transition: opacity var(--iw-fast) linear, transform var(--iw-fast) var(--iw-ease); }
.ifd-item.is-on .ifd-go { opacity: 1; transform: none; }
.ifd-empty { display: flex; flex-direction: column; align-items: center; gap: var(--s-2); padding: var(--s-6) var(--s-4); color: var(--text-muted); font-size: var(--t-body); text-align: center; }
.ifd-empty p { margin: 0; }
.ifd-foot { display: flex; align-items: center; gap: var(--s-4); padding: var(--s-2) var(--s-4); border-top: 1px solid var(--border-primary); font-size: var(--t-caption); color: var(--text-muted); }
.ifd-count { margin-left: auto; }
</style>
